/**
 * Themed file picker: click or drag-and-drop, with the chosen file shown and
 * removable.
 *
 * Deliberately NOT PriceCsvDropzone — that one hardcodes light-mode hex colors
 * (#d1d5db / #eef2ff / #6b7280) in inline styles, so it reads as a grey smudge
 * on the dark theme. This uses the Tremor semantic tokens the rest of the app
 * uses (tremor-border / tremor-content / tremor-brand), which follow the theme.
 */
import { useRef, useState } from 'react';
import { Icon } from '@tremor/react';

interface Props {
  /** Short label above the box, e.g. "XML (CFDI)". */
  label: string;
  /** Helper line shown when nothing is selected yet. */
  hint: string;
  /** The clickable second line, e.g. "seleccione un archivo". */
  browseLabel: string;
  /** `accept` attribute for the underlying input. */
  accept: string;
  /** Accessible name — the tests and screen readers address the input by this. */
  ariaLabel: string;
  file: File | null;
  onPick: (f: File | null) => void;
  disabled?: boolean;
}

function prettySize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PayableFilePicker({
  label, hint, browseLabel, accept, ariaLabel, file, onPick, disabled,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const open = () => { if (!disabled) inputRef.current?.click(); };

  const take = (f: File | undefined | null) => {
    if (!f || disabled) return;
    onPick(f);
    // Keep the native input in sync so a re-pick of the same name still fires.
    if (inputRef.current) {
      const dt = new DataTransfer();
      dt.items.add(f);
      inputRef.current.files = dt.files;
    }
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPick(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const border = dragging
    ? 'border-tremor-brand'
    : file
      ? 'border-tremor-brand/60'
      : 'border-tremor-border/80';

  return (
    <div>
      <div className="text-tremor-default font-medium text-tremor-content-strong mb-1">
        {label}
      </div>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        // The dropzone announces the VISIBLE label; the hidden input below keeps
        // the short name. Giving both the same aria-label made two controls
        // indistinguishable to anyone addressing them by name.
        aria-label={label}
        aria-disabled={disabled || undefined}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
        }}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); take(e.dataTransfer.files?.[0]); }}
        className={[
          'rounded-tremor-default border-2 border-dashed px-4 py-6 text-center transition-colors',
          border,
          dragging ? 'bg-tremor-brand-faint' : 'bg-tremor-background-muted',
          disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-tremor-brand/60',
        ].join(' ')}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          aria-label={ariaLabel}
          className="hidden"
          disabled={disabled}
          onChange={(e) => take(e.target.files?.[0])}
        />

        {file ? (
          <div className="flex items-center justify-center gap-2">
            <Icon icon={DocIcon} size="sm" />
            <span className="text-tremor-default text-tremor-content-strong truncate max-w-[16rem]">
              {file.name}
            </span>
            <span className="text-tremor-label text-tremor-content">
              {prettySize(file.size)}
            </span>
            <button
              type="button"
              onClick={clear}
              aria-label={`Quitar ${label}`}
              className="text-tremor-label text-tremor-content hover:text-tremor-content-strong underline ml-1"
            >
              Quitar
            </button>
          </div>
        ) : (
          // Icon + two deliberate lines. A single sentence wrapped mid-phrase
          // ("…aquí o / selecciona un archivo") reads as a layout accident.
          <div className="flex flex-col items-center gap-1.5">
            <UploadIcon className="h-6 w-6 text-tremor-content-subtle" />
            <div className="text-tremor-default text-tremor-content">{hint}</div>
            <div className="text-tremor-label text-tremor-brand underline">
              {browseLabel}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Small inline glyphs — avoids pulling a whole icon package in. */
function DocIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}

function UploadIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}
         strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M12 3v13" />
      <path d="m7 8 5-5 5 5" />
    </svg>
  );
}
